import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  const links = (
    <>
      <Link data-testid="nav-link-worlds" to="/worlds" onClick={close} className="hover:text-amber-200">Worlds</Link>
      <Link data-testid="nav-link-leaderboards" to="/leaderboards" onClick={close} className="hover:text-amber-200">Leaderboards</Link>
      <Link data-testid="nav-link-pricing" to="/pricing" onClick={close} className="hover:text-amber-200">Pricing</Link>
      {user && <Link data-testid="nav-link-dashboard" to="/dashboard" onClick={close} className="hover:text-amber-200">My Saga</Link>}
      {user && <Link data-testid="nav-link-profile" to="/profile" onClick={close} className="hover:text-amber-200">Profile</Link>}
      <Link data-testid="nav-link-support" to="/support" onClick={close} className="hover:text-amber-200">Support</Link>
      {user?.is_admin && <Link data-testid="nav-link-admin" to="/admin" onClick={close} className="hover:text-amber-200 text-amber-400">Admin</Link>}
    </>
  );

  return (
    <header
      data-testid="nav-header"
      className="sticky top-0 z-40 backdrop-blur-md bg-[#0f0d11]/85 border-b border-amber-700/15"
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-amber-400 focus:text-stone-900 focus:px-4 focus:py-2 focus:rounded font-ui text-sm"
      >
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-3 group flex-shrink-0">
          <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flicker" />
          <span className="font-display text-xl sm:text-2xl tracking-tight text-amber-100 group-hover:text-amber-200">
            TalesTalesTales
          </span>
        </Link>

        <nav aria-label="Primary navigation" className="hidden md:flex items-center gap-7 font-ui text-sm text-amber-100/70">
          {links}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              <span data-testid="nav-user-name" className="hidden lg:inline font-display text-amber-200/80 truncate max-w-[140px]">
                {user.display_name}
              </span>
              <Button
                data-testid="nav-logout-btn"
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex text-amber-200 hover:bg-amber-900/20"
                onClick={() => { logout(); nav("/"); }}
              >
                <LogOut className="w-4 h-4 mr-1" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <NotificationBell user={user} />              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex text-amber-200 hover:bg-amber-900/20 font-ui"
                onClick={() => nav("/login")}
              >
                Sign in
              </Button>
              <Button
                data-testid="nav-cta-btn"
                size="sm"
                className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui candle-glow text-xs sm:text-sm px-3 sm:px-4"
                onClick={() => nav("/register")}
              >
                Begin Saga
              </Button>
            </>
          )}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-amber-200 p-1.5 rounded hover:bg-amber-900/20"
            onClick={() => setOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-amber-700/15 bg-[#0f0d11]/95 backdrop-blur-md">
          <nav aria-label="Mobile navigation" className="flex flex-col gap-1 px-4 py-3 font-ui text-sm text-amber-100/85">
            {links}
            {user ? (
              <button
                data-testid="nav-mobile-logout"
                className="text-left py-2 text-amber-300/85 flex items-center gap-2"
                onClick={() => { close(); logout(); nav("/"); }}
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            ) : (
              <Link data-testid="nav-mobile-login" to="/login" onClick={close} className="py-2 text-amber-300/85">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
